import { useCallback, useEffect, useState, useRef } from "react";
import { Scissors } from "lucide-react";
import {
  Coins,
  ArrowsClockwise,
  ArrowCounterClockwise,
  CheckCircle,
  Warning,
  Sliders,
  MagnifyingGlass,
  Funnel,
  Play,
  Gear,
  Sparkle,
  ShieldCheck,
  Calendar,
  FileText,
} from "@phosphor-icons/react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

const NETWORKS = [
  "all",
  "skimlinks",
  "impact",
  "mrge",
  "partnerize",
  "cj",
  "flexoffers",
  "siteplug",
  "maxbounty",
  "admedia",
  "giddyup",
  "pubadhub",
  "shopnomix",
  "cashbackhive",
  "fatcoupon",
];

export default function AdminShaveActions() {
  const [activeTab, setActiveTab] = useState("studio"); // "studio" | "presets" | "catalog" | "batches"
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Studio filter states
  const [networkFilter, setNetworkFilter] = useState("all");
  const [pubFilter, setPubFilter] = useState("all");
  const [customPubSub, setCustomPubSub] = useState("");
  const [stateFilter, setStateFilter] = useState("APPROVED");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [shaveMode, setShaveMode] = useState("amount"); // "amount" | "count" | "all"
  const [targetAmount, setTargetAmount] = useState("6000");
  const [targetCount, setTargetCount] = useState("10");
  const [reasonInput, setReasonInput] = useState("manual_shave");

  // Candidates & Selection
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewMeta, setPreviewMeta] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [executingShave, setExecutingShave] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);

  // Catalog tab
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogTotalPages, setCatalogTotalPages] = useState(1);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogNetwork, setCatalogNetwork] = useState("all");
  const [catalogBatchFilter, setCatalogBatchFilter] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  // Batches tab
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [restoringBatchId, setRestoringBatchId] = useState(null);

  // Settings modal
  const [settingsModal, setSettingsModal] = useState(false);
  const [settings, setSettings] = useState({
    intango_auto_shave_enabled: true,
    intango_daily_count: 10,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Presets states
  const [intangoRunning, setIntangoRunning] = useState(false);
  const [skimlinksRunning, setSkimlinksRunning] = useState(false);
  const [skimlinksMode, setSkimlinksMode] = useState("amount");
  const [skimlinksTarget, setSkimlinksTarget] = useState("6000");

  const didFetchInitial = useRef(false);

  // Fetch Stats
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await API.get("/admin/shave-actions/stats");
      setStats(data || null);
      if (data?.intango) {
        setSettings({
          intango_auto_shave_enabled: data.intango.auto_shave_enabled,
          intango_daily_count: data.intango.daily_count,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shave stats");
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // Fetch Candidates for Studio
  const fetchCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const pubParam =
        pubFilter === "INTANGO"
          ? "INTANGO"
          : pubFilter === "custom"
          ? customPubSub.trim()
          : undefined;

      const { data } = await API.get("/admin/shave-actions/candidates", {
        params: {
          network: networkFilter !== "all" ? networkFilter : undefined,
          publisher_sub_id: pubParam || undefined,
          state: stateFilter !== "ALL" ? stateFilter : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          mode: shaveMode,
          target_amount: shaveMode === "amount" ? parseFloat(targetAmount) || 0 : 0,
          target_count: shaveMode === "count" ? parseInt(targetCount, 10) || 0 : 0,
          limit: 100,
        },
      });

      const list = data.candidates || [];
      setCandidates(list);
      setPreviewMeta(data);
      // Pre-select all matching candidate IDs
      const ids = new Set(list.map((c) => c.action_id || c._id));
      setSelectedIds(ids);

      if (list.length === 0) {
        toast.info("No matching unshaved conversions found for current criteria");
      } else {
        toast.success(`Loaded ${list.length} candidate conversions for review`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch candidate conversions");
    } finally {
      setLoadingCandidates(false);
    }
  };

  // Execute Shave
  const handleExecuteShave = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select at least one conversion to shave");
      return;
    }
    setExecutingShave(true);
    try {
      const { data } = await API.post("/admin/shave-actions/execute", {
        action_ids: Array.from(selectedIds),
        reason: reasonInput || "manual_shave",
        mode: shaveMode,
        target_amount: shaveMode === "amount" ? parseFloat(targetAmount) || 0 : 0,
        target_count: shaveMode === "count" ? parseInt(targetCount, 10) || 0 : 0,
      });

      toast.success(
        `Successfully shaved ${data.shaved_count} conversions ($${(
          data.total_payout_usd || 0
        ).toFixed(2)} payout) into shave_actions!`
      );
      setConfirmModal(false);
      setCandidates([]);
      setSelectedIds(new Set());
      setPreviewMeta(null);
      fetchStats();
      if (activeTab === "catalog") fetchCatalog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to execute shave");
    } finally {
      setExecutingShave(false);
    }
  };

  // Fetch Catalog
  const fetchCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const { data } = await API.get("/admin/shave-actions/shaved", {
        params: {
          page: catalogPage,
          page_size: 50,
          network: catalogNetwork !== "all" ? catalogNetwork : undefined,
          batch_id: catalogBatchFilter || undefined,
          search: catalogSearch || undefined,
        },
      });
      setCatalogItems(data.items || []);
      setCatalogTotal(data.total || 0);
      setCatalogTotalPages(data.total_pages || 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shaved catalog");
    } finally {
      setLoadingCatalog(false);
    }
  }, [catalogPage, catalogNetwork, catalogBatchFilter, catalogSearch]);

  // Fetch Batches
  const fetchBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const { data } = await API.get("/admin/shave-actions/batches");
      setBatches(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shave batches");
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  // Restore single action
  const handleRestoreAction = async (actionId) => {
    if (
      !window.confirm(
        `Restore conversion '${actionId}' back to active actions?\n\nThis will make it visible again in publisher reports and payouts.`
      )
    )
      return;
    setRestoringId(actionId);
    try {
      const { data } = await API.post("/admin/shave-actions/restore", {
        action_ids: [actionId],
      });
      toast.success(data.detail || "Action restored to live actions collection");
      fetchCatalog();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore action");
    } finally {
      setRestoringId(null);
    }
  };

  // Restore entire batch
  const handleRestoreBatch = async (batchId) => {
    if (
      !window.confirm(
        `RESTORE ENTIRE BATCH ${batchId}?\n\nAll conversions in this shave batch will be moved back to the active actions collection.`
      )
    )
      return;
    setRestoringBatchId(batchId);
    try {
      const { data } = await API.post("/admin/shave-actions/restore", {
        batch_id: batchId,
      });
      toast.success(data.detail || "Batch restored to live actions collection");
      fetchBatches();
      fetchStats();
      if (activeTab === "catalog") fetchCatalog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore batch");
    } finally {
      setRestoringBatchId(null);
    }
  };

  // Trigger Intango Run
  const handleRunIntango = async (force = false) => {
    setIntangoRunning(true);
    try {
      const { data } = await API.post("/admin/shave-actions/intango-daily-run", {
        count: settings.intango_daily_count || 10,
        dry_run: false,
        force,
      });
      if (data.skipped) {
        toast.info(`Intango Shave: Skipped (${data.reason})`);
      } else {
        toast.success(
          `Intango Shave: Successfully shaved ${data.shaved_count} conversions ($${(
            data.total_payout_usd || 0
          ).toFixed(2)})`
        );
      }
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Intango shave run failed");
    } finally {
      setIntangoRunning(false);
    }
  };

  // Trigger Skimlinks Run
  const handleRunSkimlinks = async (dryRun = false) => {
    setSkimlinksRunning(true);
    try {
      const targetVal = parseFloat(skimlinksTarget) || 6000;
      const { data } = await API.post("/admin/shave-actions/skimlinks-shave", {
        mode: skimlinksMode,
        target_amount: skimlinksMode === "amount" ? targetVal : 6000,
        target_count: skimlinksMode === "count" ? parseInt(skimlinksTarget, 10) || 6000 : 6000,
        dry_run: dryRun,
      });

      if (dryRun) {
        toast.info(
          `Skimlinks Preview: Found ${data.candidate_count} candidates totaling $${(
            data.total_payout_usd || 0
          ).toFixed(2)} payout`
        );
      } else {
        toast.success(
          `Skimlinks Shave: Successfully shaved ${data.shaved_count} conversions ($${(
            data.total_payout_usd || 0
          ).toFixed(2)} payout)!`
        );
        fetchStats();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skimlinks shave failed");
    } finally {
      setSkimlinksRunning(false);
    }
  };

  // Save Settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await API.post("/admin/shave-actions/settings", settings);
      toast.success("Shave automation settings saved");
      setSettingsModal(false);
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Toggle selection in table
  const toggleSelectId = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(candidates.map((c) => c.action_id || c._id)));
    }
  };

  // Initial load
  useEffect(() => {
    if (didFetchInitial.current) return;
    didFetchInitial.current = true;
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === "catalog") fetchCatalog();
    if (activeTab === "batches") fetchBatches();
  }, [activeTab, fetchCatalog, fetchBatches]);

  // Selected totals computation
  const selectedPayoutTotal = candidates
    .filter((c) => selectedIds.has(c.action_id || c._id))
    .reduce((acc, c) => acc + (c.payout_usd_calc || c.payout_usd || 0), 0);

  const selectedAmountTotal = candidates
    .filter((c) => selectedIds.has(c.action_id || c._id))
    .reduce((acc, c) => acc + (c.amount_usd_calc || c.amount_usd || 0), 0);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-rose-600 text-white flex items-center justify-center shadow-sm">
              <Scissors className="w-5 h-5" weight="bold" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                  Shave Actions
                </h1>
                <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200 font-mono">
                  Admin Exclusive
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Divert underpaid conversions from <code className="text-slate-700 bg-slate-100 px-1 py-0.5 rounded">actions</code> into <code className="text-slate-700 bg-slate-100 px-1 py-0.5 rounded">shave_actions</code> to reduce publisher liabilities
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStats}
            disabled={loadingStats}
            className="text-xs text-slate-600 gap-1.5"
          >
            <ArrowsClockwise className={`w-3.5 h-3.5 ${loadingStats ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsModal(true)}
            className="text-xs text-slate-700 gap-1.5 border-slate-300"
          >
            <Gear className="w-3.5 h-3.5" />
            Auto-Shave Settings
          </Button>
        </div>
      </div>

      {/* KPI Stats Ribbon */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
        <div className="bg-slate-900 text-white rounded-xl p-4 shadow-sm relative overflow-hidden">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
            Total Shaved Actions
          </div>
          <div className="text-2xl font-black mt-1 tracking-tight">
            {(stats?.total_shaved_count || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
            <span className="text-emerald-400 font-semibold">+{stats?.shaved_today_count || 0}</span> today
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Diverted Payout</span>
            <Coins className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 mt-1 tracking-tight">
            ${(stats?.total_shaved_payout_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Liability withheld from pubs
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Diverted Sales</span>
            <FileText className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 mt-1 tracking-tight">
            ${(stats?.total_shaved_amount_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            GMV of shaved orders
          </div>
        </div>

        {/* Intango Card */}
        <div className="bg-gradient-to-br from-indigo-50/70 to-white border border-indigo-200/80 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold text-indigo-900 uppercase tracking-wider">
              Intango Daily
            </div>
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 ${
                stats?.intango?.auto_shave_enabled
                  ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                  : "bg-slate-100 text-slate-600 border-slate-300"
              }`}
            >
              {stats?.intango?.auto_shave_enabled ? "Auto: ON" : "Paused"}
            </Badge>
          </div>
          <div className="text-xl font-black text-indigo-950 mt-1">
            {stats?.intango?.daily_count || 10} / day
          </div>
          <div className="text-[10px] text-indigo-700 mt-1 flex items-center justify-between">
            <span>{stats?.intango?.is_completed_today ? "✅ Today: Completed" : "⏳ Pending today"}</span>
            <button
              onClick={() => handleRunIntango(true)}
              disabled={intangoRunning}
              className="font-bold underline hover:text-indigo-900 cursor-pointer disabled:opacity-50"
            >
              {intangoRunning ? "Running..." : "Run 10 Now"}
            </button>
          </div>
        </div>

        {/* Skimlinks Card */}
        <div className="bg-gradient-to-br from-rose-50/70 to-white border border-rose-200/80 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold text-rose-900 uppercase tracking-wider">
              Skimlinks Shaved
            </div>
            <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-300 px-1.5 py-0">
              Preset ~6k
            </Badge>
          </div>
          <div className="text-xl font-black text-rose-950 mt-1">
            ${(stats?.skimlinks?.shaved_payout_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-rose-700 mt-1 flex items-center justify-between">
            <span>{(stats?.skimlinks?.shaved_count || 0).toLocaleString()} actions total</span>
            <button
              onClick={() => {
                setActiveTab("presets");
              }}
              className="font-bold underline hover:text-rose-900 cursor-pointer"
            >
              Open Shaver →
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("studio")}
          className={`flex items-center gap-2 px-4 py-2.5 font-medium text-xs border-b-2 transition-colors cursor-pointer ${
            activeTab === "studio"
              ? "border-rose-600 text-rose-700 font-bold"
              : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          <Scissors className="w-4 h-4" />
          Manual Shave Studio
        </button>

        <button
          onClick={() => setActiveTab("presets")}
          className={`flex items-center gap-2 px-4 py-2.5 font-medium text-xs border-b-2 transition-colors cursor-pointer ${
            activeTab === "presets"
              ? "border-rose-600 text-rose-700 font-bold"
              : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          <Sparkle className="w-4 h-4" />
          Intango & Skimlinks Presets
        </button>

        <button
          onClick={() => setActiveTab("catalog")}
          className={`flex items-center gap-2 px-4 py-2.5 font-medium text-xs border-b-2 transition-colors cursor-pointer ${
            activeTab === "catalog"
              ? "border-rose-600 text-rose-700 font-bold"
              : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          <FileText className="w-4 h-4" />
          Shaved Actions Catalog ({stats?.total_shaved_count || 0})
        </button>

        <button
          onClick={() => setActiveTab("batches")}
          className={`flex items-center gap-2 px-4 py-2.5 font-medium text-xs border-b-2 transition-colors cursor-pointer ${
            activeTab === "batches"
              ? "border-rose-600 text-rose-700 font-bold"
              : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          Batch Audit History
        </button>
      </div>

      {/* TAB 1: MANUAL SHAVE STUDIO */}
      {activeTab === "studio" && (
        <div className="space-y-5">
          {/* Filter & Criteria Panel */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                <Funnel className="w-4 h-4 text-slate-600" />
                Select Candidates from Live Actions
              </div>
              <div className="text-xs text-slate-500">
                Step 1: Set filters & target amount/count · Step 2: Preview & select · Step 3: Shave
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Network</Label>
                <select
                  value={networkFilter}
                  onChange={(e) => setNetworkFilter(e.target.value)}
                  className="mt-1.5 w-full text-xs h-9 px-2.5 bg-white border border-slate-200 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                >
                  {NETWORKS.map((n) => (
                    <option key={n} value={n}>
                      {n === "all" ? "All Networks" : n.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700">Publisher Scope</Label>
                <select
                  value={pubFilter}
                  onChange={(e) => setPubFilter(e.target.value)}
                  className="mt-1.5 w-full text-xs h-9 px-2.5 bg-white border border-slate-200 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                >
                  <option value="all">All Publishers</option>
                  <option value="INTANGO">Intango (72LZBW)</option>
                  <option value="custom">Specific Sub ID...</option>
                </select>
                {pubFilter === "custom" && (
                  <Input
                    placeholder="Enter Sub ID (e.g. 72LZBW)"
                    value={customPubSub}
                    onChange={(e) => setCustomPubSub(e.target.value)}
                    className="mt-1.5 text-xs h-8 font-mono uppercase"
                  />
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700">Date Range (Event Date)</Label>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="text-xs h-9 px-2"
                  />
                  <span className="text-slate-400 text-xs">to</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="text-xs h-9 px-2"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700">Conversion State</Label>
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="mt-1.5 w-full text-xs h-9 px-2.5 bg-white border border-slate-200 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-slate-900"
                >
                  <option value="APPROVED">APPROVED Only</option>
                  <option value="PENDING">PENDING Only</option>
                  <option value="ALL">All Non-Reversed</option>
                </select>
              </div>
            </div>

            {/* Shave Mode Strategy */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold text-slate-800">Shave Strategy:</span>
                <div className="flex items-center gap-3 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="shaveMode"
                      value="amount"
                      checked={shaveMode === "amount"}
                      onChange={() => setShaveMode("amount")}
                      className="text-rose-600"
                    />
                    <span>By Target Payout ($)</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="shaveMode"
                      value="count"
                      checked={shaveMode === "count"}
                      onChange={() => setShaveMode("count")}
                      className="text-rose-600"
                    />
                    <span>By Action Count</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="shaveMode"
                      value="all"
                      checked={shaveMode === "all"}
                      onChange={() => setShaveMode("all")}
                      className="text-rose-600"
                    />
                    <span>All Matching</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {shaveMode === "amount" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 font-medium">Target Amount ($):</span>
                    <Input
                      type="number"
                      step="50"
                      value={targetAmount}
                      onChange={(e) => setTargetAmount(e.target.value)}
                      className="w-28 h-8 text-xs font-mono font-bold"
                    />
                  </div>
                )}
                {shaveMode === "count" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 font-medium">Target Count:</span>
                    <Input
                      type="number"
                      min="1"
                      value={targetCount}
                      onChange={(e) => setTargetCount(e.target.value)}
                      className="w-24 h-8 text-xs font-mono font-bold"
                    />
                  </div>
                )}
                <Button
                  onClick={fetchCandidates}
                  disabled={loadingCandidates}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8 px-4 gap-1.5 shadow-sm"
                >
                  <MagnifyingGlass className={`w-3.5 h-3.5 ${loadingCandidates ? "animate-spin" : ""}`} />
                  Preview Candidates
                </Button>
              </div>
            </div>
          </div>

          {/* Candidate Table Section */}
          {candidates.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              {/* Table Action Bar */}
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === candidates.length && candidates.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded text-rose-600 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-800">
                    Selected: {selectedIds.size} of {candidates.length} candidate(s)
                  </span>
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-800 border-emerald-300">
                    Selected Payout: ${selectedPayoutTotal.toFixed(2)}
                  </Badge>
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-800 border-blue-300">
                    Selected Sales: ${selectedAmountTotal.toFixed(2)}
                  </Badge>
                </div>

                <div className="flex items-center gap-2.5">
                  <Input
                    placeholder="Audit reason (e.g. skimlinks_underpayment)"
                    value={reasonInput}
                    onChange={(e) => setReasonInput(e.target.value)}
                    className="text-xs h-8 w-60"
                  />
                  <Button
                    onClick={() => setConfirmModal(true)}
                    disabled={selectedIds.size === 0 || executingShave}
                    className="bg-rose-600 hover:bg-rose-700 text-white text-xs h-8 px-4 gap-1.5 shadow-sm font-bold"
                  >
                    <Scissors className="w-3.5 h-3.5" weight="bold" />
                    Shave {selectedIds.size} Selected Actions
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto max-h-[500px]">
                <table className="w-full text-left border-collapse text-xs">
                  <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10 border-b border-slate-200">
                    <tr>
                      <th className="p-3 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === candidates.length && candidates.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded text-rose-600 cursor-pointer"
                        />
                      </th>
                      <th className="p-3 font-semibold">Action ID</th>
                      <th className="p-3 font-semibold">Date</th>
                      <th className="p-3 font-semibold">Network</th>
                      <th className="p-3 font-semibold">Sub ID</th>
                      <th className="p-3 font-semibold">Campaign / Brand</th>
                      <th className="p-3 font-semibold text-right">Sale Amount ($)</th>
                      <th className="p-3 font-semibold text-right">Payout ($)</th>
                      <th className="p-3 font-semibold text-center">State</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono">
                    {candidates.map((c) => {
                      const id = c.action_id || c._id;
                      const isSelected = selectedIds.has(id);
                      const payout = c.payout_usd_calc || c.payout_usd || c.payout || 0;
                      const amount = c.amount_usd_calc || c.amount_usd || c.amount || 0;

                      return (
                        <tr
                          key={id}
                          onClick={() => toggleSelectId(id)}
                          className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                            isSelected ? "bg-rose-50/50" : ""
                          }`}
                        >
                          <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectId(id)}
                              className="rounded text-rose-600 cursor-pointer"
                            />
                          </td>
                          <td className="p-3 font-medium text-slate-900">{c.action_id || c._id}</td>
                          <td className="p-3 text-slate-600 whitespace-nowrap">
                            {(c.event_date || "").slice(0, 19).replace("T", " ")}
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-[10px] uppercase font-bold px-1.5 py-0">
                              {c.network || "unknown"}
                            </Badge>
                          </td>
                          <td className="p-3 text-slate-700 font-bold">{c.sub_id1 || c.sub_id3 || "—"}</td>
                          <td className="p-3 text-slate-800 font-sans truncate max-w-xs" title={c.campaign_name}>
                            {c.campaign_name || c.campaign_id || "Unattributed"}
                          </td>
                          <td className="p-3 text-right text-slate-700">${Number(amount).toFixed(2)}</td>
                          <td className="p-3 text-right font-bold text-emerald-700">${Number(payout).toFixed(2)}</td>
                          <td className="p-3 text-center">
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 ${
                                c.state === "APPROVED"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                  : "bg-amber-50 text-amber-700 border-amber-300"
                              }`}
                            >
                              {c.state || "APPROVED"}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: PRESETS (Intango & Skimlinks) */}
      {activeTab === "presets" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: Intango 10 Daily Conversions */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                  <Sparkle className="w-4 h-4" weight="bold" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Intango Daily Auto-Shaver</h3>
                  <p className="text-xs text-slate-500">Every day move 10 conversions to shave_actions</p>
                </div>
              </div>
              <Badge
                variant="outline"
                className={
                  stats?.intango?.auto_shave_enabled
                    ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                    : "bg-slate-100 text-slate-600"
                }
              >
                {stats?.intango?.auto_shave_enabled ? "Enabled" : "Paused"}
              </Badge>
            </div>

            <div className="space-y-3 text-xs text-slate-600">
              <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 space-y-1">
                <div className="font-semibold text-indigo-950">Active Intango Configuration:</div>
                <div>• Publisher Sub IDs: <code className="font-mono font-bold text-indigo-900">72LZBW</code></div>
                <div>• Daily Shave Target: <strong className="text-indigo-900">{settings.intango_daily_count || 10} conversions</strong></div>
                <div>• Schedule: Runs nightly at UTC midnight in the daily scheduler cycle</div>
                <div>• Total Intango Shaved: <strong>{stats?.intango?.shaved_count || 0} conversions (${(stats?.intango?.shaved_payout_usd || 0).toFixed(2)})</strong></div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="font-medium text-slate-700">Status for Today ({new Date().toISOString().slice(0, 10)}):</span>
                <span className="font-bold">
                  {stats?.intango?.is_completed_today ? (
                    <span className="text-emerald-700 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Already Shaved Today
                    </span>
                  ) : (
                    <span className="text-amber-700">⏳ Pending Tonight</span>
                  )}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={() => handleRunIntango(true)}
                disabled={intangoRunning}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold gap-1.5 shadow-sm"
              >
                <Play className="w-3.5 h-3.5" />
                {intangoRunning ? "Shaving..." : "Run 10 Actions Shave Now"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSettingsModal(true)}
                className="text-xs text-slate-700"
              >
                Configure
              </Button>
            </div>
          </div>

          {/* Card 2: Skimlinks ~6k Amount / Count Shave */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                  <Scissors className="w-4 h-4" weight="bold" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Skimlinks ~6k Shave Tool</h3>
                  <p className="text-xs text-slate-500">Remove around 6k transactions / payout into shave_actions</p>
                </div>
              </div>
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">
                Network Preset
              </Badge>
            </div>

            <div className="space-y-3 text-xs text-slate-600">
              <div className="bg-rose-50/60 border border-rose-100 rounded-lg p-3 space-y-1">
                <div className="font-semibold text-rose-950">Skimlinks Shave Scope:</div>
                <div>• Network: <code className="font-mono font-bold text-rose-900">skimlinks</code></div>
                <div>• Total Skimlinks Shaved: <strong>{stats?.skimlinks?.shaved_count || 0} conversions (${(stats?.skimlinks?.shaved_payout_usd || 0).toFixed(2)})</strong></div>
              </div>

              <div className="space-y-2 border-t border-slate-100 pt-3">
                <Label className="text-xs font-semibold text-slate-700">Choose 6k Mode:</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="skimMode"
                      value="amount"
                      checked={skimlinksMode === "amount"}
                      onChange={() => {
                        setSkimlinksMode("amount");
                        setSkimlinksTarget("6000");
                      }}
                      className="text-rose-600"
                    />
                    <span>Target Payout Amount ($6,000)</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="skimMode"
                      value="count"
                      checked={skimlinksMode === "count"}
                      onChange={() => {
                        setSkimlinksMode("count");
                        setSkimlinksTarget("6000");
                      }}
                      className="text-rose-600"
                    />
                    <span>Target Count (6,000 actions)</span>
                  </label>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <span className="text-slate-500">Target Value:</span>
                  <Input
                    type="number"
                    value={skimlinksTarget}
                    onChange={(e) => setSkimlinksTarget(e.target.value)}
                    className="w-32 h-8 text-xs font-mono font-bold"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRunSkimlinks(true)}
                disabled={skimlinksRunning}
                className="text-xs text-slate-700 gap-1.5"
              >
                <MagnifyingGlass className="w-3.5 h-3.5" />
                Dry Run Preview
              </Button>
              <Button
                onClick={() => {
                  if (
                    window.confirm(
                      `Are you sure you want to shave ~${skimlinksTarget} (${skimlinksMode}) of Skimlinks conversions?\n\nThey will be removed from actions and moved to shave_actions.`
                    )
                  ) {
                    handleRunSkimlinks(false);
                  }
                }}
                disabled={skimlinksRunning}
                className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold gap-1.5 shadow-sm"
              >
                <Scissors className="w-3.5 h-3.5" weight="bold" />
                {skimlinksRunning ? "Shaving..." : "Execute Skimlinks 6k Shave"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: SHAVED ACTIONS CATALOG */}
      {activeTab === "catalog" && (
        <div className="space-y-4">
          {/* Catalog Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 max-w-md">
              <MagnifyingGlass className="w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search Action ID, Campaign, Batch ID, or Reason..."
                value={catalogSearch}
                onChange={(e) => {
                  setCatalogSearch(e.target.value);
                  setCatalogPage(1);
                }}
                className="text-xs h-9"
              />
            </div>

            <div className="flex items-center gap-2">
              <select
                value={catalogNetwork}
                onChange={(e) => {
                  setCatalogNetwork(e.target.value);
                  setCatalogPage(1);
                }}
                className="text-xs h-9 px-2.5 bg-white border border-slate-200 rounded-md shadow-sm"
              >
                <option value="all">All Networks</option>
                {NETWORKS.filter((n) => n !== "all").map((n) => (
                  <option key={n} value={n}>
                    {n.toUpperCase()}
                  </option>
                ))}
              </select>

              <Button
                variant="outline"
                size="sm"
                onClick={fetchCatalog}
                disabled={loadingCatalog}
                className="text-xs h-9 px-3 gap-1"
              >
                <ArrowsClockwise className={`w-3.5 h-3.5 ${loadingCatalog ? "animate-spin" : ""}`} />
                Reload
              </Button>
            </div>
          </div>

          {/* Catalog Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-slate-100 text-slate-700 border-b border-slate-200">
                  <tr>
                    <th className="p-3 font-semibold">Shaved At</th>
                    <th className="p-3 font-semibold">Reason</th>
                    <th className="p-3 font-semibold">Batch ID</th>
                    <th className="p-3 font-semibold">Action ID</th>
                    <th className="p-3 font-semibold">Network</th>
                    <th className="p-3 font-semibold">Publisher Sub</th>
                    <th className="p-3 font-semibold">Brand / Offer</th>
                    <th className="p-3 font-semibold text-right">Sale Amount ($)</th>
                    <th className="p-3 font-semibold text-right">Payout ($)</th>
                    <th className="p-3 font-semibold text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-mono">
                  {catalogItems.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="p-8 text-center text-slate-400 font-sans">
                        {loadingCatalog ? "Loading shave_actions..." : "No records found in shave_actions collection."}
                      </td>
                    </tr>
                  ) : (
                    catalogItems.map((item) => {
                      const id = item.action_id || item._id;
                      const payout = item.payout_usd || item.payout || 0;
                      const amount = item.amount_usd || item.amount || 0;

                      return (
                        <tr key={item._id} className="hover:bg-slate-50 transition-colors">
                          <td className="p-3 text-slate-600 whitespace-nowrap">
                            {(item.shaved_at || "").slice(0, 19).replace("T", " ")}
                          </td>
                          <td className="p-3">
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 font-sans ${
                                item.shave_reason?.includes("intango")
                                  ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                  : item.shave_reason?.includes("skimlinks")
                                  ? "bg-rose-50 text-rose-700 border-rose-200"
                                  : "bg-slate-100 text-slate-700 border-slate-300"
                              }`}
                            >
                              {item.shave_reason || "shaved"}
                            </Badge>
                          </td>
                          <td className="p-3 text-slate-500 text-[11px] font-mono">
                            {item.shave_batch_id?.slice(0, 14)}...
                          </td>
                          <td className="p-3 font-medium text-slate-900">{item.action_id || item._id}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-[10px] uppercase font-bold px-1.5 py-0">
                              {item.network || "—"}
                            </Badge>
                          </td>
                          <td className="p-3 text-slate-800 font-bold">{item.sub_id1 || item.sub_id3 || "—"}</td>
                          <td className="p-3 text-slate-800 font-sans truncate max-w-xs" title={item.campaign_name}>
                            {item.campaign_name || item.campaign_id || "Unattributed"}
                          </td>
                          <td className="p-3 text-right text-slate-600">${Number(amount).toFixed(2)}</td>
                          <td className="p-3 text-right font-bold text-rose-700">${Number(payout).toFixed(2)}</td>
                          <td className="p-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestoreAction(id)}
                              disabled={restoringId === id}
                              className="text-[11px] h-7 px-2 text-indigo-700 hover:text-indigo-900 hover:bg-indigo-50"
                              title="Restore back to active actions"
                            >
                              <ArrowCounterClockwise className={`w-3 h-3 mr-1 ${restoringId === id ? "animate-spin" : ""}`} />
                              Restore
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {catalogTotalPages > 1 && (
              <div className="p-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
                <div>
                  Showing {catalogItems.length} of {catalogTotal.toLocaleString()} shaved conversions
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={catalogPage <= 1}
                    onClick={() => setCatalogPage((p) => Math.max(1, p - 1))}
                    className="text-xs h-7 px-2.5"
                  >
                    Previous
                  </Button>
                  <span>
                    Page {catalogPage} of {catalogTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={catalogPage >= catalogTotalPages}
                    onClick={() => setCatalogPage((p) => Math.min(catalogTotalPages, p + 1))}
                    className="text-xs h-7 px-2.5"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: BATCH AUDIT HISTORY */}
      {activeTab === "batches" && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-slate-100 text-slate-700 border-b border-slate-200">
                  <tr>
                    <th className="p-3 font-semibold">Shave Timestamp</th>
                    <th className="p-3 font-semibold">Batch ID</th>
                    <th className="p-3 font-semibold">Triggered By</th>
                    <th className="p-3 font-semibold">Reason</th>
                    <th className="p-3 font-semibold">Mode</th>
                    <th className="p-3 font-semibold text-right">Actions Count</th>
                    <th className="p-3 font-semibold text-right">Total Payout ($)</th>
                    <th className="p-3 font-semibold text-right">Total Sales ($)</th>
                    <th className="p-3 font-semibold text-center">Status</th>
                    <th className="p-3 font-semibold text-center">Batch Rollback</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-mono">
                  {batches.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="p-8 text-center text-slate-400 font-sans">
                        {loadingBatches ? "Loading batch history..." : "No shave batch runs recorded yet."}
                      </td>
                    </tr>
                  ) : (
                    batches.map((b) => (
                      <tr key={b._id || b.batch_id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 text-slate-600 whitespace-nowrap">
                          {(b.shaved_at || "").slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="p-3 font-bold text-slate-900">{b.batch_id}</td>
                        <td className="p-3 text-slate-600 font-sans">{b.shaved_by || "admin"}</td>
                        <td className="p-3 font-sans">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {b.reason}
                          </Badge>
                        </td>
                        <td className="p-3 text-slate-600 font-sans">{b.mode || "manual"}</td>
                        <td className="p-3 text-right font-bold text-slate-900">{b.action_count}</td>
                        <td className="p-3 text-right font-bold text-rose-700">
                          ${(b.total_payout_usd || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-right text-slate-600">${(b.total_amount_usd || 0).toFixed(2)}</td>
                        <td className="p-3 text-center">
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-1.5 py-0 font-sans ${
                              b.status === "restored"
                                ? "bg-amber-50 text-amber-700 border-amber-300"
                                : "bg-emerald-50 text-emerald-700 border-emerald-300"
                            }`}
                          >
                            {b.status || "completed"}
                          </Badge>
                        </td>
                        <td className="p-3 text-center">
                          {b.status !== "restored" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRestoreBatch(b.batch_id)}
                              disabled={restoringBatchId === b.batch_id}
                              className="text-[11px] h-7 px-2 text-rose-700 border-rose-200 hover:bg-rose-50"
                            >
                              <ArrowCounterClockwise className="w-3 h-3 mr-1" />
                              Restore Batch
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMATION DIALOG MODAL */}
      <Dialog open={confirmModal} onOpenChange={setConfirmModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2 text-rose-700">
              <Warning className="w-5 h-5 text-rose-600" />
              Confirm Action Shave
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Please review the conversions you are about to shave into <code className="font-mono">shave_actions</code>.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 text-xs text-slate-700">
            <div className="flex justify-between">
              <span className="font-medium text-slate-500">Conversions Count:</span>
              <span className="font-bold text-slate-900">{selectedIds.size} action(s)</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-slate-500">Total Payout Withheld:</span>
              <span className="font-bold text-emerald-700">${selectedPayoutTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-slate-500">Total Merchandise Volume:</span>
              <span className="font-bold text-slate-900">${selectedAmountTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-slate-500">Reason / Tag:</span>
              <span className="font-mono text-slate-800">{reasonInput || "manual_shave"}</span>
            </div>
          </div>

          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2.5 rounded-md">
            ⚠️ <strong>Notice:</strong> These conversions will be deleted from the active <code className="font-mono">actions</code> table and moved to <code className="font-mono">shave_actions</code>. Publishers will not see them in reports or receive payouts for them. You can restore them anytime from the Shaved Actions Catalog.
          </p>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setConfirmModal(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleExecuteShave}
              disabled={executingShave}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
            >
              {executingShave ? "Shaving..." : "Confirm & Shave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUTOMATION SETTINGS MODAL */}
      <Dialog open={settingsModal} onOpenChange={setSettingsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2 text-slate-900">
              <Gear className="w-5 h-5 text-indigo-600" />
              Auto-Shave Automation Settings
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Configure daily scheduled background shave tasks.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 text-xs">
            <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <Label className="font-bold text-slate-900">Intango Daily Auto-Shave</Label>
                <p className="text-[11px] text-slate-500">
                  Automatically move conversions daily at UTC midnight
                </p>
              </div>
              <Switch
                checked={settings.intango_auto_shave_enabled}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, intango_auto_shave_enabled: checked }))
                }
              />
            </div>

            <div>
              <Label className="font-semibold text-slate-700">Intango Daily Conversions Target</Label>
              <Input
                type="number"
                min="1"
                max="1000"
                value={settings.intango_daily_count}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    intango_daily_count: parseInt(e.target.value, 10) || 10,
                  }))
                }
                className="mt-1 h-9 text-xs font-mono font-bold"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Default: 10 conversions per day.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setSettingsModal(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold"
            >
              {savingSettings ? "Saving..." : "Save Settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
